// @ts-nocheck
/**
 * 鍵関連ユーティリティ
 * @module twinKey/core/keySystem/keyUtils
 */

// ============================================================
// 鍵データ変換
// ============================================================

/**
 * コレクションカードを鍵として変換
 * @param {Object} collectionCard - コレクション内のカード
 * @param {Object} cardData - カードマスターデータ
 * @returns {Object} 鍵オブジェクト
 */
export function toKeyCard(collectionCard, cardData) {
  return {
    cardId: collectionCard.cardId,
    uniqueId: collectionCard.uniqueId,
    rarity: collectionCard.rarity,
    name: cardData?.name || collectionCard.name || '不明なカード',
    attribute: cardData?.attribute || 'なし',
    type: cardData?.type || 'monster',
    attack: cardData?.attack || 0,
    hp: cardData?.hp || 0,
    cost: cardData?.cost || 0,
    category: cardData?.category || '',
    effect: cardData?.effect || '',
    forbidden: cardData?.forbidden || false,
  };
}

/**
 * 鍵からカード表示用情報を取得
 * @param {Object} key - 鍵オブジェクト
 * @returns {Object} 表示用情報
 */
export function getKeyDisplayInfo(key) {
  if (!key) {
    return {
      name: '未選択',
      rarity: '',
      attribute: '',
      attributeIcon: '❓',
      rarityColor: '#888',
    };
  }

  const attributeIcons = {
    '炎': '🔥',
    '水': '💧',
    '光': '✨',
    '闇': '🌙',
    '原始': '🌿',
    '未来': '⚡',
    'なし': '⚪',
  };

  const rarityColors = {
    'C': '#888888',
    'UC': '#4a4a4a',
    'R': '#3498db',
    'SR': '#9b59b6',
    'UR': '#f39c12',
    'HR': '#e74c3c',
    'SEC': '#e91e63',
    'ALT': '#00bcd4',
    'SP': '#ff5722',
    'GR': '#ffd700',
  };

  return {
    name: key.name,
    rarity: key.rarity,
    attribute: key.attribute,
    attributeIcon: attributeIcons[key.attribute] || '⚪',
    rarityColor: rarityColors[key.rarity] || '#888',
    isForbidden: key.forbidden,
    type: key.type,
  };
}

// ============================================================
// 鍵選択補助
// ============================================================

/**
 * コレクションから鍵として使用可能なカードをフィルタ
 * @param {Array} collection - コレクション
 * @param {Object} filter - フィルタ条件
 * @returns {Array} フィルタ済みカード
 */
export function filterValidKeys(collection, filter = {}) {
  let filtered = collection.filter(card => card.count > 0);

  if (filter.attribute) {
    filtered = filtered.filter(card => card.attribute === filter.attribute);
  }

  if (filter.rarity) {
    filtered = filtered.filter(card => card.rarity === filter.rarity);
  }

  if (filter.type) {
    filtered = filtered.filter(card => card.type === filter.type);
  }

  if (filter.excludeCardId) {
    filtered = filtered.filter(card => card.cardId !== filter.excludeCardId);
  }

  if (filter.excludeUniqueId) {
    filtered = filtered.filter(card => card.uniqueId !== filter.excludeUniqueId);
  }

  if (filter.search) {
    const searchLower = filter.search.toLowerCase();
    filtered = filtered.filter(card =>
      (card.name && card.name.toLowerCase().includes(searchLower)) ||
      (card.cardId && card.cardId.toLowerCase().includes(searchLower))
    );
  }

  return filtered;
}

/**
 * 鍵のおすすめを取得
 * @param {Object} key1 - 既に選択された鍵1
 * @param {Array} collection - コレクション
 * @param {number} limit - 取得数
 * @returns {Array} おすすめカード
 */
export function getRecommendedKeys(key1, collection, limit = 5) {
  if (!key1) {
    // 最初の鍵未選択：高レアリティ順
    return [...collection]
      .filter(card => card.count > 0)
      .sort((a, b) => getRarityOrder(b.rarity) - getRarityOrder(a.rarity))
      .slice(0, limit);
  }

  // 2枚目の鍵選択：シナジーのあるカードを優先
  const candidates = collection.filter(card =>
    card.count > 0 && card.uniqueId !== key1.uniqueId
  );

  // シナジースコアでソート
  return candidates
    .map(card => ({
      ...card,
      synergyScore: calculateSynergyScore(key1, card),
    }))
    .sort((a, b) => b.synergyScore - a.synergyScore)
    .slice(0, limit);
}

/**
 * シナジースコアを計算
 * @param {Object} key1 - 鍵1
 * @param {Object} key2 - 鍵2
 * @returns {number} スコア
 */
function calculateSynergyScore(key1, key2) {
  let score = 0;

  // 同属性ボーナス
  if (key1.attribute === key2.attribute) {
    score += 20;
  }

  // 補完属性ボーナス
  const complementary = {
    '炎': ['原始', '光'],
    '水': ['闇', '炎'],
    '光': ['闇', '炎'],
    '闇': ['光', '水'],
    '原始': ['炎', '闇'],
    '未来': ['未来'],
  };

  if (complementary[key1.attribute]?.includes(key2.attribute)) {
    score += 15;
  }

  // レアリティマッチングボーナス
  if (key1.rarity === key2.rarity) {
    score += 5;
  }

  return score;
}

/**
 * レアリティの順序値を取得
 * @param {string} rarity - レアリティ
 * @returns {number} 順序値
 */
function getRarityOrder(rarity) {
  const order = {
    'C': 1, 'UC': 2, 'R': 3, 'SR': 4, 'UR': 5,
    'HR': 6, 'SEC': 7, 'ALT': 7, 'SP': 8, 'GR': 9,
  };
  return order[rarity] || 0;
}

// ============================================================
// 鍵履歴
// ============================================================

/**
 * 鍵使用履歴を追加
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object} key1 - 鍵1
 * @param {Object} key2 - 鍵2
 * @returns {Object} 更新されたプレイヤーデータ
 */
export function addKeyHistory(playerData, key1, key2) {
  const twinKeyData = playerData.twinKey || {};
  const history = twinKeyData.keyHistory || [];

  const newEntry = {
    timestamp: Date.now(),
    key1: {
      cardId: key1.cardId,
      rarity: key1.rarity,
      name: key1.name,
    },
    key2: {
      cardId: key2.cardId,
      rarity: key2.rarity,
      name: key2.name,
    },
  };

  // 最大100件保持
  const updatedHistory = [newEntry, ...history].slice(0, 100);

  return {
    ...playerData,
    twinKey: {
      ...twinKeyData,
      keyHistory: updatedHistory,
    },
  };
}

/**
 * 鍵使用統計を取得
 * @param {Object} playerData - プレイヤーデータ
 * @returns {Object} 統計情報
 */
export function getKeyStatistics(playerData) {
  const history = playerData.twinKey?.keyHistory || [];

  const cardUsage = {};
  const attributeUsage = {};
  const rarityUsage = {};

  for (const entry of history) {
    // カード使用回数
    cardUsage[entry.key1.cardId] = (cardUsage[entry.key1.cardId] || 0) + 1;
    cardUsage[entry.key2.cardId] = (cardUsage[entry.key2.cardId] || 0) + 1;

    // 属性使用回数
    attributeUsage[entry.key1.attribute] = (attributeUsage[entry.key1.attribute] || 0) + 1;
    attributeUsage[entry.key2.attribute] = (attributeUsage[entry.key2.attribute] || 0) + 1;

    // レアリティ使用回数
    rarityUsage[entry.key1.rarity] = (rarityUsage[entry.key1.rarity] || 0) + 1;
    rarityUsage[entry.key2.rarity] = (rarityUsage[entry.key2.rarity] || 0) + 1;
  }

  // 最も使用されたカードTOP5
  const topCards = Object.entries(cardUsage)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([cardId, count]) => ({ cardId, count }));

  return {
    totalExpeditions: history.length,
    topCards,
    attributeUsage,
    rarityUsage,
  };
}

// ============================================================
// 鍵組み合わせID
// ============================================================

/**
 * 鍵ペアのユニークIDを生成
 * @param {Object} key1 - 鍵1
 * @param {Object} key2 - 鍵2
 * @returns {string} ユニークID
 */
export function generateKeyPairId(key1, key2) {
  // カードIDをソートして順序非依存にする
  const ids = [
    `${key1.cardId}_${key1.rarity}`,
    `${key2.cardId}_${key2.rarity}`,
  ].sort();

  return `pair_${ids[0]}_${ids[1]}`;
}

/**
 * 鍵ペアIDをパース
 * @param {string} pairId - ペアID
 * @returns {{ key1Id: string, key1Rarity: string, key2Id: string, key2Rarity: string } | null}
 */
export function parseKeyPairId(pairId) {
  const match = pairId.match(/^pair_([^_]+)_([^_]+)_([^_]+)_([^_]+)$/);
  if (!match) return null;

  return {
    key1Id: match[1],
    key1Rarity: match[2],
    key2Id: match[3],
    key2Rarity: match[4],
  };
}

// ============================================================
// カードフィルタリング
// ============================================================

/**
 * 属性でカードをフィルタ
 * @param {Array} cards - カード配列
 * @param {string} attribute - 属性（'all' または特定属性）
 * @returns {Array} フィルタ済みカード
 */
export function filterCardsByAttribute(cards, attribute) {
  if (!attribute || attribute === 'all') {
    return cards;
  }
  return cards.filter(card => card.attribute === attribute);
}

/**
 * レアリティでカードをフィルタ
 * @param {Array} cards - カード配列
 * @param {string} rarity - レアリティ（'all' または特定レアリティ）
 * @returns {Array} フィルタ済みカード
 */
export function filterCardsByRarity(cards, rarity) {
  if (!rarity || rarity === 'all') {
    return cards;
  }
  return cards.filter(card => card.rarity === rarity);
}

/**
 * カードをソート
 * @param {Array} cards - カード配列
 * @param {string} sortBy - ソートキー ('name' | 'rarity' | 'attribute' | 'cost' | 'attack' | 'hp')
 * @param {string} order - ソート順 ('asc' | 'desc')
 * @returns {Array} ソート済みカード
 */
export function sortCards(cards, sortBy = 'name', order = 'asc') {
  const sorted = [...cards].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'name':
        comparison = (a.name || '').localeCompare(b.name || '');
        break;
      case 'rarity':
        comparison = getRarityOrder(a.rarity) - getRarityOrder(b.rarity);
        break;
      case 'attribute':
        comparison = (a.attribute || '').localeCompare(b.attribute || '');
        break;
      case 'cost':
        comparison = (a.cost || 0) - (b.cost || 0);
        break;
      case 'attack':
        comparison = (a.attack || 0) - (b.attack || 0);
        break;
      case 'hp':
        comparison = (a.hp || 0) - (b.hp || 0);
        break;
      default:
        comparison = 0;
    }

    return order === 'desc' ? -comparison : comparison;
  });

  return sorted;
}
